<template>
  <div class="visit-page-form">
    <div class="visit-page-form__header">
      <Button
        icon="far fa-arrow-left"
        class="p-button-text p-button-rounded"
        @click="$emit('cancel')"
      />
      <div class="visit-page-form__title">
        <h2>{{ t('flows.operators.visitPage.label') }}</h2>
        <span class="visit-page-form__subtitle">
          {{ t('flows.operatorKinds.trigger') }}
        </span>
      </div>
      <Button
        icon="far fa-times"
        class="p-button-text p-button-rounded"
        @click="$emit('close')"
      />
    </div>

    <div class="visit-page-form__content">
      <div class="visit-page-form__field">
        <label class="visit-page-form__label">
          {{ t('flows.operators.visitPage.titleLabel') }}
        </label>
        <InputText
          v-model="localData.custom_title"
          class="w-full"
          :class="{ 'p-invalid': formValidation?.custom_title?.$errors?.length }"
        />
        <small
          v-if="formValidation?.custom_title?.$errors?.length"
          class="p-error"
        >
          {{ formValidation.custom_title.$errors[0].$message }}
        </small>
      </div>

      <div class="visit-page-form__field">
        <label class="visit-page-form__label">
          {{ t('flows.operators.visitPage.descriptionLabel') }}
        </label>
        <InputText
          v-model="localData.custom_description"
          class="w-full"
        />
      </div>

      <div class="visit-page-form__field">
        <label class="visit-page-form__label">
          {{ t('flows.operators.visitPage.triggerLocation') }}
        </label>
        <div class="visit-page-form__options">
          <div
            v-for="option in locationOptions"
            :key="option.value"
            class="visit-page-form__option"
            :class="{ 'visit-page-form__option--selected': localData.trigger_location === option.value }"
            @click="localData.trigger_location = option.value"
          >
            <i :class="option.icon" />
            <div>
              <span class="visit-page-form__option-label">{{ option.label }}</span>
              <span class="visit-page-form__option-desc">{{ option.description }}</span>
            </div>
          </div>
        </div>
        <small
          v-if="formValidation?.trigger_location?.$errors?.length"
          class="p-error"
        >
          {{ formValidation.trigger_location.$errors[0].$message }}
        </small>
      </div>
    </div>

    <div class="visit-page-form__footer">
      <Button
        :label="t('flows.operatorPanel.cancel')"
        class="p-button-text"
        @click="$emit('cancel')"
      />
      <Button
        :label="t('flows.operatorPanel.addStep')"
        class="p-button-success"
        @click="$emit('submit')"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import type { VisitPageData } from './index';

export default defineComponent({
  name: 'VisitPageForm',

  components: {
    Button,
    InputText,
  },

  props: {
    modelValue: {
      type: Object as PropType<VisitPageData>,
      default: () => ({
        custom_title: '',
        custom_description: '',
        trigger_location: 1,
      }),
    },
    formValidation: {
      type: Object,
      default: () => ({}),
    },
  },

  emits: ['update:modelValue', 'cancel', 'close', 'submit'],

  setup(props, { emit }) {
    const { t } = useI18n();

    const localData = ref<VisitPageData>({ ...props.modelValue });

    const locationOptions = [
      {
        value: 1,
        label: t('flows.operators.visitPage.allPages'),
        description: t('flows.operators.visitPage.allPagesDesc'),
        icon: 'far fa-globe',
      },
      {
        value: 0,
        label: t('flows.operators.visitPage.specificPages'),
        description: t('flows.operators.visitPage.specificPagesDesc'),
        icon: 'far fa-filter',
      },
    ];

    watch(localData, (newData) => {
      emit('update:modelValue', newData);
    }, { deep: true });

    watch(() => props.modelValue, (newValue) => {
      if (newValue !== localData.value) {
        localData.value = { ...newValue };
      }
    });

    return {
      t,
      localData,
      locationOptions,
    };
  },
});
</script>

<style lang="scss" scoped>
.visit-page-form {
  display: flex;
  flex-direction: column;
  height: 100%;

  &__header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    border-bottom: 1px solid #e5e7eb;

    .p-button {
      width: 32px !important;
      height: 32px !important;
      min-width: 32px !important;
    }
  }

  &__title {
    flex: 1;

    h2 {
      margin: 0;
      font-size: 0.875rem;
      font-weight: 600;
      color: #1f2937;
    }
  }

  &__subtitle {
    font-size: 0.75rem;
    color: #6b7280;
  }

  &__content {
    flex: 1;
    padding: 1rem;
    overflow-y: auto;
  }

  &__field {
    margin-bottom: 1.5rem;
  }

  &__label {
    display: block;
    margin-bottom: 0.5rem;
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
  }

  &__options {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  &__option {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    cursor: pointer;
    transition: all 0.15s ease;

    i {
      font-size: 1rem;
      color: #6b7280;
      margin-top: 0.125rem;
    }

    &:hover {
      border-color: #3b82f6;
      background: #f0f9ff;
    }

    &--selected {
      border-color: #3b82f6;
      background: #eff6ff;

      i {
        color: #3b82f6;
      }
    }
  }

  &__option-label {
    display: block;
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
  }

  &__option-desc {
    display: block;
    font-size: 0.75rem;
    color: #6b7280;
    margin-top: 0.125rem;
  }

  &__footer {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    padding: 1rem;
    border-top: 1px solid #e5e7eb;
  }
}
</style>
